import { Account, Customer, CustomerAccount, Promotions } from '@inyova/models';

import { createAction, props } from '@ngrx/store';

import { PromoCampaign, PromoCampaignItem } from '@shared/models/DigitalPromo';
import { ABTest, CreatedPromotions, CreatedPromotionsError, NewPromotions, ReferralRewards } from '@shared/models/Profile';
import { ThirdPartyServiceItem } from '@shared/models/ThirdPartService';

export const fetchReminder = createAction('[Root] Fetch reminder');
export const fetchReminderSuccess = createAction('[Root] Fetch reminder success', props<{ reminder: Date }>());
export const fetchReminderFail = createAction('[Root] Fetch reminder fail');

export const updateReminder = createAction('[Root] Update reminder date and time', props<{ reminder: Date }>());
export const updateReminderSuccess = createAction('[Root] Update reminder date and time success', props<{ reminder: Date }>());
export const updateReminderFail = createAction('[Root] Update reminder date and time fail');

export const showReminderDialog = createAction('[Root] Show reminder dialog', props<{ reminderModalOpened: boolean }>());

export const fetchCustomer = createAction('[Root] Fetch customer accounts');
export const fetchCustomerSuccess = createAction(
  '[Root] Fetch customer accounts success',
  props<{ customer: Customer; selectActiveAccount: boolean }>()
);
export const fetchCustomerFail = createAction('[Root] Fetch customer accounts fail');

export const fetchCustomerSelectActiveAccount = createAction(
  '[Root] Fetch customer select active account',
  props<{ customerAccountDetail: CustomerAccount }>()
);

export const fetchCustomerAccountDetail = createAction('[Root] Fetch customer account detail', props<{ accountID: string }>());
export const fetchCustomerAccountDetailSuccess = createAction(
  '[Root] Fetch customer account detail success',
  props<{ customerAccountDetail: Account }>()
);
export const fetchCustomerAccountDetailFail = createAction('[Root] Fetch customer account detail fail');

export const createNewAccount = createAction('[Root] Create new account', props<{ kind: string; appLocation: string; owner_name?: string }>());
export const createNewAccountFail = createAction('[Root] Create new account fail');

export const switchAccount = createAction('[Root] Switch account', props<{ id: string; redirectUrl: string }>());
export const setSwitchAccountLoading = createAction('[Root] Set switch account loading', props<{ show: boolean; message: string }>());

export const sendVerificationCode = createAction('[Root] Send request to send Twilio verification code to customer phone');
export const sendVerificationCodeSuccess = createAction(
  '[Root] Send request to send Twilio verification code to customer phone success',
  props<{ status: string; date: Date }>()
);
export const sendVerificationCodeFail = createAction('[Root] Send request to send Twilio verification code to customer phone fail');

export const resendVerificationEmail = createAction('[Root] Resend verification email', (customerId: number) => ({
  customerId
}));
export const resendVerificationEmailSuccess = createAction(
  '[Root] Resend verification email success',
  (cooldownUntil: number, customerId: number) => ({
    cooldownUntil,
    customerId
  })
);
export const resendVerificationEmailFail = createAction('[Root] Resend verification email fail', (error) => ({ error }));

export const loadResendEmailCooldown = createAction('[Root] Load resend email cooldown');
export const loadResendEmailCooldownSuccess = createAction('[Root] Load resend email cooldown success', (cooldownUntil: number) => ({
  cooldownUntil
}));

export const verifyCodeStatusReset = createAction('[Root] Verify correctness of verification code status reset');
export const verifyVerificationCode = createAction('[Root] Verify correctness of verification code', props<{ code: string }>());
export const verifyVerificationCodeSuccess = createAction(
  '[Root] Verify correctness of verification code success',
  props<{ status: string; date: Date }>()
);
export const verifyVerificationCodeFail = createAction('[Root] Verify correctness of verification code fail');

export const showPromotionsDialog = createAction('[Root] Show promotions dialog', props<{ show: boolean }>());

export const referralsPageOpened = createAction('[Root] Referral page opened', (forced = false) => ({ forced }));

export const getPromotions = createAction('[Account] Get promotions', (forced = false) => ({ forced }));
export const getPromotionsSuccess = createAction('[Account] Get promotions success', props<{ data: Promotions }>());
export const getPromotionsFail = createAction('[Account] Get promotions fail');

export const updatePromotions = createAction('[Root] Update new promotions', props<{ payload: NewPromotions }>());
export const updatePromotionsSuccess = createAction('[Root] Update new promotions success', props<{ response: CreatedPromotions }>());
export const updatePromotionsFail = createAction('[Root] Update new promotions fail', props<{ response: CreatedPromotionsError[] }>());

export const resetPromotionsStatus = createAction('[Root] Reset promotions status');

export const fetchThirdParties = createAction('[Root] fetch third party services', props<{ service: string }>());
export const fetchThirdPartiesSuccess = createAction('[Root] fetch third party services success', props<{ response: ThirdPartyServiceItem[] }>());
export const fetchThirdPartiesFail = createAction('[Root] fetch third party services fail');

export const fetchReferralRewards = createAction('[Root] fetch referral rewards', props<{ referralCode: string }>());
export const fetchReferralRewardsSuccess = createAction('[Root] set referral rewards success', props<{ response: ReferralRewards }>());
export const fetchReferralRewardsFail = createAction('[Root] set referral rewards fail');

export const fetchABTests = createAction('[Root] Fetch AB tests');
export const fetchABTestsSuccess = createAction('[Root] Fetch AB tests success', props<{ data: ABTest[] }>());
export const fetchABTestsFail = createAction('[Root] Fetch AB tests fail');

export const showMobilePromo = createAction('[Root] Show mobile promotion banner', props<{ show: boolean }>());
export const toggleCIAccountActive = createAction('[Root] Toggle CI Account active', props<{ show: boolean }>());

export const updateSpDeposit = createAction('[Root] Update SP deposit', props<{ deposit: number }>());
export const updateSpDepositSuccess = createAction('[Root] Update SP deposit success');
export const updateSpDepositFail = createAction('[Root] Update SP deposit fail');
export const toggleDepositSuccessDialog = createAction('[Root] Toggle deposit success dialog', props<{ show: boolean }>());

export const setIsLanguageChange = createAction('[Root] Set language change', props<{ change: boolean }>());

export const toggleOddPageActive = createAction('[Root] Toggle Odd page active', props<{ oddPageActive: boolean }>());

export const toggleNewAccountIsLoading = createAction('[Root] Toggle new account is loading', props<{ isLoading: boolean }>());

export const getPromoCampaigns = createAction('[Root] Get promo campaigns');
export const getPromoCampaignsSuccess = createAction('[Root] Get promo campaigns success', props<{ data: PromoCampaign }>());
export const getPromoCampaignsFail = createAction('[Root] Get promo campaigns fail');

export const setCampaignParticipation = createAction('[Root] Set Campaign participation', props<{ participation: boolean; id: number }>());
export const setCampaignParticipationSuccess = createAction('[Root] Set Campaign participation success', props<{ data: PromoCampaignItem[] }>());
export const setCampaignParticipationFail = createAction('[Root] Set Campaign participation fail');

export const submitGreennessSurvey = createAction(
  '[Root] Submit greenness survey',
  props<{ data: { greenness_score?: number; net_worth?: string; annual_income?: string } }>()
);
export const submitGreennessSurveySuccess = createAction('[Root] Submit greenness survey success', props<{ success: boolean }>());
export const submitGreennessSurveyFail = createAction('[Root] Submit greenness survey fail', props<{ success: boolean }>());
export const hideSubmitGreennessSurveyNotification = createAction('[Root] Hide greenness survey notification');

export const submitOnboardingGreennessSurvey = createAction('[Root] Submit onboarding greenness survey', props<{ data: { greenness_score? } }>());
export const submitOnboardingGreennessSurveySuccess = createAction(
  '[Root] Submit onboarding greenness survey success',
  props<{ success: boolean }>()
);
export const submitOnboardingGreennessSurveyFail = createAction('[Root] Submit onboarding greenness survey fail', props<{ success: boolean }>());
